import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import GetAppIcon from "@mui/icons-material/GetApp";
import { OkIcon, ExpiredIcon, BufferIcon } from "../../components/CourseStatusIcon";

import UserTitle from "../../components/UserTitle";
import TableComponent from "../../components/TableComponent";
import Button from "../../components/Button";
import { StyledTabs } from "../../components/Base";
import { ExportActions } from "../../components/Certificate/Actions";

import { getUserCertificates, getShortUserInfo } from "../../api/user.deprecated";
import useUser from "../../hooks/useUser";

import { formatDate } from "../../services/formatters";
import { APPROVE_STATUS } from "../../constants";
import { openAllDownloadWindow } from "../../api";

/**
 * "Declined: <reason>", or the plain declined text for an older decline without a reason (CREW-602).
 * The reason is user text rendered as React text or a title: i18next must not HTML-escape it too
 * ("doesn&#39;t").
 */
export const declinedText = (t, reason) =>
  reason
    ? t("enums.certificate_status.declined_with_reason", { reason, interpolation: { escapeValue: false } })
    : t("enums.certificate_status.rejected");

const CertificateStatusIcon = ({ status, declineReason }) => {
  const { t } = useTranslation();

  if (!status) return null;

  let tooltipText = "";
  let icon = <OkIcon />;

  if (status === APPROVE_STATUS.BUFFER) {
    tooltipText = t("enums.certificate_status.awaiting_approval");
    icon = <BufferIcon />;
  } else if (status === APPROVE_STATUS.DECLINED) {
    tooltipText = declinedText(t, declineReason);
    icon = <ExpiredIcon />;
  }

  return <Button disabled={status === "approve"} title={tooltipText} icon={icon} />;
};

/** Under the course name of a declined upload: that it was declined, and why (CREW-602). */
const DeclinedNote = ({ certificate }) => {
  const { t } = useTranslation();
  if (certificate.approveStatus !== APPROVE_STATUS.DECLINED) return null;
  return (
    <Typography variant="caption" component="div" sx={{ color: "error.main" }}>
      {declinedText(t, certificate.declineReason)}
    </Typography>
  );
};

// A declined upload grants no validity, so it shows none.
const validUntil = (certificate, t) => {
  if (certificate.approveStatus === APPROVE_STATUS.DECLINED) return "-";
  return certificate.validPeriod ? formatDate(certificate.validPeriod) : t("certificate.never_expires");
};

const filterOutTheoreticalTrainingCertificates = (certificates) =>
  certificates.filter((certificate) => !certificate.isPartOfTraining);

const AllUserCertificates = ({ match, history }) => {
  const { t } = useTranslation();
  const user = useUser();
  const { userId } = match.params;
  const uid = userId || user.id;

  const [selectedTab, setSelectedTab] = useState(0);
  const [userCertificates, setUserCertificates] = useState(null);
  const [userCustomCertificates, setUserCustomCertificates] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    (async () => {
      if (uid) {
        const userInfo = await getShortUserInfo(uid);
        // includeDeclined: the member sees declined uploads too, marked with the reviewer's reason.
        const { certificates, customCertificates } = await getUserCertificates(uid, null, true, false, true);

        // Add __actions for downloadable certificates
        const addDownloadActions = (cert) => ({
          ...cert,
          actions: {
            downloadableCertificate: cert,
          },
        });

        // Deduplicate by uploadId
        const deduplicateByUploadId = (certs) => {
          const seen = new Map();
          return certs.filter((cert) => {
            const key = cert.uploadId || `${cert.id}_${cert.crewId || 0}`;
            if (seen.has(key)) {
              return false;
            }
            seen.set(key, true);
            return true;
          });
        };

        const filteredCertificates = filterOutTheoreticalTrainingCertificates(certificates);
        setUserCertificates(deduplicateByUploadId(filteredCertificates).map(addDownloadActions));

        const customCertsWithFlag = customCertificates.map((c) => ({
          ...c,
          isCustom: true,
        }));
        setUserCustomCertificates(deduplicateByUploadId(customCertsWithFlag).map(addDownloadActions));

        setUserInfo(userInfo);
      }
    })();
  }, [uid]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h4">
          <Button title={t("navigate_back")} onClick={() => history.goBack()} icon={<NavigateBeforeIcon />} />
          {t("all_user_certificates")}
        </Typography>
        <Box>
          {userInfo && <UserTitle id={userInfo.id} name={userInfo.fullname} email={userInfo.email} />}
          {(userCertificates?.filter((c) => !!c.certificateFile || c.type === "moodle").length ||
            userCustomCertificates?.filter((c) => !!c.certificateFile).length) && (
            <Box
              sx={{
                m: "1.25rem 0",
              }}
            >
              <Button title={t("download_all_certificates")} onClick={() => openAllDownloadWindow(uid)}>
                {t("download_all_certificates")} <GetAppIcon />
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          mb: 2,
        }}
      >
        <StyledTabs
          variant="fullWidth"
          value={selectedTab}
          indicatorColor="primary"
          onChange={(_, value) => setSelectedTab(value)}
        >
          <Tab label={t("all_moodle_courses")} />
          <Tab label={t("all_custom_certificates")} />
        </StyledTabs>
      </Box>
      {selectedTab === 0 && userCertificates?.length > 0 && (
        <TableComponent
          header={[
            { title: t("course_id"), style: { width: "5%" }, sort: "id", sortType: "number" },
            {
              title: t("course_full_name"),
              style: { width: "15%" },
              sort: "name",
            },
            {
              title: t("completed_date"),
              style: { width: "15%" },
              sort: "completedDate",
              sortType: "date",
            },
            {
              title: t("valid_until"),
              style: { width: "15%" },
              sort: "validPeriod",
              sortType: "date",
            },
            { title: t("company"), style: { width: "15%" } },
            { title: t("actions"), style: { width: "10%" } },
          ]}
          pagination
          items={userCertificates}
          search={["name", "id"]}
          defaultSort={{ sortBy: "completedDate", sortDirection: "desc" }}
          renderChildren={(certificate) => (
            <TableRow key={certificate.uploadId || `${certificate.id}_${certificate.crewId || 0}`}>
              <TableCell>{certificate.id}</TableCell>
              <TableCell>
                {certificate.name}
                <DeclinedNote certificate={certificate} />
              </TableCell>
              <TableCell>{certificate.completedDate ? formatDate(certificate.completedDate) : "-"} </TableCell>
              <TableCell>{validUntil(certificate, t)}</TableCell>
              <TableCell>
                {certificate.crewName && (
                  <>
                    <CertificateStatusIcon
                      status={certificate?.approveStatus}
                      declineReason={certificate?.declineReason}
                    />
                    {certificate.crewName}
                  </>
                )}
              </TableCell>
              <TableCell>
                <ExportActions userId={uid} certificate={certificate} isCustom={false} />
              </TableCell>
            </TableRow>
          )}
        />
      )}
      {selectedTab === 1 && userCustomCertificates?.length > 0 && (
        <TableComponent
          header={[
            { title: t("course_id"), style: { width: "5%" }, sort: "id", sortType: "number" },
            {
              title: t("course_full_name"),
              style: { width: "20%" },
              sort: "name",
            },
            {
              title: t("completed_date"),
              style: { width: "15%" },
              sort: "completedDate",
              sortType: "date",
            },
            {
              title: t("valid_until"),
              style: { width: "20%" },
              sort: "validPeriod",
              sortType: "date",
            },
            { title: t("company"), style: { width: "15%" }, sort: "crewName" },
            { title: t("actions"), style: { width: "10%" } },
          ]}
          pagination
          search={["name", "id"]}
          items={userCustomCertificates}
          defaultSort={{ sortBy: "completedDate", sortDirection: "desc" }}
          renderChildren={(certificate) => (
            <TableRow key={certificate.uploadId || `${certificate.id}_${certificate.crewId || 0}`}>
              <TableCell>{certificate.id}</TableCell>
              <TableCell>
                {certificate.name}
                <DeclinedNote certificate={certificate} />
              </TableCell>
              <TableCell>{certificate.completedDate ? formatDate(certificate.completedDate) : "-"} </TableCell>
              <TableCell>{validUntil(certificate, t)}</TableCell>
              <TableCell>
                {certificate.crewName && (
                  <>
                    <CertificateStatusIcon
                      status={certificate?.approveStatus}
                      declineReason={certificate?.declineReason}
                    />
                    {certificate.crewName}
                  </>
                )}
              </TableCell>
              <TableCell>
                <ExportActions userId={uid} certificate={certificate} isCustom={true} />
              </TableCell>
            </TableRow>
          )}
        />
      )}
    </>
  );
};

export default AllUserCertificates;
